import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Crown, ArrowDown } from "lucide-react";
import useCapacity from "@/lib/useCapacity";
import { trackInitiateCheckout, prepareStripeCheckout } from "@/lib/metaPixel";
import { STRIPE_VIP_URL, EVENT_SOLD_OUT } from "@/lib/tickets";
import EmailCapture from "./EmailCapture";

const HALL_BG =
  "https://customer-assets.emergentagent.com/job_afe027c3-a313-4e93-840c-5cce45d2042c/artifacts/ggfwzfb9_IMG_6391.webp";
const IVO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_real-estate-varna/artifacts/jq8zrtmd_Screenshot%202026-07-15%20at%2011.58.35.png";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 + i * 0.08 },
  }),
};

const TARGET = new Date("2026-10-24T09:00:00+03:00").getTime();

function calcLeft() {
  const diff = Math.max(0, TARGET - Date.now());
  return {
    d: Math.floor(diff / (1000 * 60 * 60 * 24)),
    h: Math.floor((diff / (1000 * 60 * 60)) % 24),
    m: Math.floor((diff / (1000 * 60)) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const [loaded, setLoaded] = useState(false);
  const { tier, vip_remaining, vip_total } = useCapacity();
  const soldOut = EVENT_SOLD_OUT || tier === "sold_out";
  const vipTotal = vip_total || 50;
  const vipSold = Math.max(0, vipTotal - (vip_remaining || 0));
  const vipPercent = vipTotal > 0 ? Math.min(100, Math.round((vipSold / vipTotal) * 100)) : 0;
  const vipGone = soldOut || vip_remaining <= 0;
  const [left, setLeft] = useState(calcLeft());

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 60);
    const id = setInterval(() => setLeft(calcLeft()), 1000);
    return () => {
      clearTimeout(t);
      clearInterval(id);
    };
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goVipCheckout = () => {
    const checkoutUrl = prepareStripeCheckout({
      url: STRIPE_VIP_URL,
      ticketType: "vip",
      value: 119,
      currency: "EUR",
      contentName: "VIP Достъп — Играта на Имотите Варна",
    });
    trackInitiateCheckout({
      value: 119,
      currency: "EUR",
      content_name: "VIP Достъп — Играта на Имотите Варна",
      content_ids: ["vip-119"],
    });
    window.open(checkoutUrl, "_blank", "noopener,noreferrer");
  };

  // Ivo portrait + KSB main partner badge (shared between layouts)
  const portrait = (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={loaded ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 1, delay: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid="hero-portrait"
      className={`relative ${EVENT_SOLD_OUT ? "col-span-12 lg:col-span-5" : "col-span-12 lg:col-span-4 hidden md:block"}`}
    >
      <div className="relative mx-auto max-w-[380px] lg:max-w-none">
        <div
          className="relative overflow-hidden frame-shadow"
          style={{ aspectRatio: "4/5" }}
        >
          <div
            className="w-full h-full bg-cover"
            style={{
              backgroundImage: `url(${IVO_PORTRAIT})`,
              backgroundPosition: "center 12%",
              backgroundSize: "140% auto",
              backgroundRepeat: "no-repeat",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-[#0B1F3D] via-[#0B1F3D]/60 to-transparent pointer-events-none" />
        </div>
        <div className="mt-4 flex items-center justify-center gap-3">
          <div className="w-2 h-2 rounded-full bg-[#F5892E] animate-pulse" />
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/85">
            Организирано от Иво Димовски
          </span>
        </div>

        {/* KSB main partner badge */}
        <div className="mt-5 bg-white/95 backdrop-blur px-4 py-3 flex items-center gap-4 border border-[#F5EBD9]/10">
          <img
            src="/partners/ksb.png"
            alt="КСБ — Камара на строителите в България"
            className="h-8 sm:h-9 w-auto object-contain shrink-0"
          />
          <div className="border-l border-[#0B1F3D]/15 pl-4 min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#F5892E] leading-none">
              Главен партньор
            </div>
            <div className="font-display uppercase text-sm text-[#0B1F3D] mt-1 leading-none">
              КСБ · Варна
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className={`relative ${EVENT_SOLD_OUT ? "min-h-[100svh]" : "h-[100svh]"} min-h-[720px] w-full overflow-hidden text-[#F5EBD9] bg-[#0B1F3D] flex flex-col`}
    >
      {/* Packed hall background — hidden in sold-out mode (clean dark canvas) */}
      {!EVENT_SOLD_OUT && (
        <>
          <motion.div
            style={{ y: bgY, backgroundImage: `url(${HALL_BG})` }}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          />
          <div className="absolute inset-0 bg-[#0B1F3D]/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3D]/20 via-[#0B1F3D]/45 to-[#0B1F3D]" />
        </>
      )}
      {/* Sunset amber glow */}
      <div className="absolute inset-0 sunset-glow pointer-events-none" />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative z-10 flex-1 flex flex-col max-w-[1440px] w-full mx-auto px-6 sm:px-10 lg:px-16 pt-32 sm:pt-36 pb-6">
        {/* Big meta line — selling mode only */}
        {!EVENT_SOLD_OUT && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="mb-4 sm:mb-6 flex flex-wrap items-center gap-4"
          >
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-[#F5892E]" />
              <span className="font-serif text-xl sm:text-2xl lg:text-3xl text-[#F5EBD9] tracking-tight">
                24.10.2026 · Хотел Черно Море · Варна
              </span>
            </div>

            <motion.button
              onClick={goVipCheckout}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={loaded ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.7, delay: 0.35 }}
              data-testid="hero-capacity-pill"
              className="relative inline-flex items-center gap-3 sm:gap-4 pl-5 pr-7 py-4 sm:py-5 rounded-full hover:scale-[1.03] transition-transform border-2 animate-pulse bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C] text-[#0B1F3D] border-[#0B1F3D]/20 shadow-[0_0_60px_-5px_rgba(224,185,92,0.9)]"
            >
              <span className="relative flex h-4 w-4 sm:h-5 sm:w-5">
                <span className="absolute inline-flex h-full w-full rounded-full animate-ping bg-[#0B1F3D]/50" />
                <span className="relative inline-flex h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-[#0B1F3D]" />
              </span>
              <motion.span
                animate={{ scale: [1, 1.07, 1] }}
                transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
                className="font-display uppercase tracking-[0.06em] text-2xl sm:text-3xl lg:text-4xl font-bold leading-none"
              >
                {vipGone ? "Разпродадено" : (
                  <>Остават само <span>{vip_remaining}</span> VIP билета</>
                )}
              </motion.span>
            </motion.button>
          </motion.div>
        )}

        {/* Main split: text + portrait */}
        <div className={`grid grid-cols-12 gap-6 lg:gap-10 flex-1 ${EVENT_SOLD_OUT ? "items-start" : "items-center"}`}>
          {EVENT_SOLD_OUT ? (
            <>
              {/* Left: sold-out statement + message (centered, photo on the right) */}
              <div className="col-span-12 lg:col-span-7 flex flex-col justify-center text-center py-6 sm:py-8">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.1 }}
                  className="flex items-center justify-center gap-4 mb-7"
                >
                  <span className="h-px w-10 bg-[#E5484D]" />
                  <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.32em] text-[#F5EBD9]/70">
                    24.10.2026 · Хотел Черно Море · Варна
                  </span>
                  <span className="h-px w-10 bg-[#E5484D]" />
                </motion.div>

                <div className="mask-line">
                  <motion.h1
                    variants={line}
                    initial="hidden"
                    animate={loaded ? "show" : "hidden"}
                    custom={0}
                    data-testid="hero-soldout-title"
                    className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl xl:text-[4.2rem] leading-[1.02] tracking-tight"
                  >
                    Събитието „Играта на Имотите" на 24.10 е{" "}
                    <span className="text-[#E5484D]" style={{ textShadow: "0 10px 60px rgba(229,72,77,0.45)" }}>
                      разпродадено
                    </span>
                  </motion.h1>
                </div>

                <div className="mask-line mt-5">
                  <motion.p
                    variants={line}
                    initial="hidden"
                    animate={loaded ? "show" : "hidden"}
                    custom={1}
                    className="text-base sm:text-lg text-[#F5EBD9]/75 leading-relaxed max-w-[56ch] mx-auto"
                  >
                    Билети вече не се предлагат. Не очаквахме този огромен интерес и благодарим
                    на всички, които ще бъдат с нас! Моля, прочетете програмата по-долу, за да
                    сте навреме за регистрацията.
                  </motion.p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.6 }}
                  className="mt-8 flex flex-col items-center gap-4"
                >
                  <button
                    onClick={() => scrollTo("program")}
                    data-testid="hero-cta-program"
                    className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm sm:text-base font-bold tracking-wide uppercase border-2 border-[#F5892E] text-[#F5892E] hover:bg-[#F5892E] hover:text-[#0B1F3D] transition-all duration-300"
                  >
                    Виж програмата
                    <ArrowDown className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <div className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.24em] text-[#F5EBD9]/70" data-testid="hero-registration-note">
                    Регистрацията отваря в <span className="text-[#F5892E] font-bold">8:45 ч.</span> · Хотел Черно Море, Варна
                  </div>
                </motion.div>

                {/* Pulsing countdown to the event start */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.75 }}
                  className="mt-9 flex flex-col items-center"
                  data-testid="hero-countdown"
                >
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[#F5892E] animate-ping" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#F5892E]" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60">
                      До началото остават
                    </span>
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    className="flex items-start gap-3 sm:gap-5"
                  >
                    {[
                      { v: left.d, l: "дни" },
                      { v: left.h, l: "часа" },
                      { v: left.m, l: "мин" },
                      { v: left.s, l: "сек" },
                    ].map((u, i) => (
                      <div key={u.l} className="flex items-start gap-3 sm:gap-5">
                        <div className="text-center">
                          <div className="font-display text-4xl sm:text-5xl text-[#F5892E] tabular-nums leading-none" style={{ textShadow: "0 6px 30px rgba(245,137,46,0.4)" }}>
                            {String(u.v).padStart(2, "0")}
                          </div>
                          <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.28em] text-[#F5EBD9]/50">
                            {u.l}
                          </div>
                        </div>
                        {i < 3 && (
                          <div className="font-display text-3xl sm:text-4xl text-[#F5892E]/40 leading-none animate-pulse">:</div>
                        )}
                      </div>
                    ))}
                  </motion.div>
                </motion.div>
              </div>

              {/* Right: Ivo portrait + KSB badge, next to the sold-out text */}
              {portrait}

              {/* 25.10 teaser — full width, centered, newsletter style */}
              <div className="col-span-12 text-center flex flex-col items-center justify-center pt-10 sm:pt-14 border-t border-[#F5EBD9]/10 mt-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.5 }}
                  className="flex items-center justify-center gap-4 mb-6"
                >
                  <span className="h-px w-10 bg-[#F5892E]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
                    Специално обявление · 25.10
                  </span>
                  <span className="h-px w-10 bg-[#F5892E]" />
                </motion.div>

                <div className="mask-line">
                  <motion.h2
                    variants={line}
                    initial="hidden"
                    animate={loaded ? "show" : "hidden"}
                    custom={2}
                    className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl"
                  >
                    НА 25.10 обявяваме{" "}
                    <span className="text-[#F5892E]">нещо специално.</span>
                  </motion.h2>
                </div>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.75 }}
                  className="mt-6 text-base sm:text-lg text-[#F5EBD9]/70 max-w-[52ch] mx-auto mb-10 leading-relaxed"
                >
                  Остави имейла си, за да не го пропуснеш — ще научиш пръв, преди всички
                  останали. Никакъв спам — само важното.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                  id="notify"
                  className="scroll-mt-32 w-full flex justify-center"
                >
                  <EmailCapture />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={loaded ? { opacity: 1 } : {}}
                  transition={{ duration: 0.9, delay: 1.1 }}
                  className="mt-8 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#F5EBD9]/40"
                >
                  Това издание е разпродадено — следващото не чака.
                </motion.div>
              </div>
            </>
          ) : (
            <>
              <div className="col-span-12 lg:col-span-8">
                <div className="mask-line">
                  <motion.div
                    variants={line}
                    initial="hidden"
                    animate={loaded ? "show" : "hidden"}
                    custom={0}
                    className="font-display uppercase text-[#F5EBD9]/85 text-4xl sm:text-5xl lg:text-6xl leading-none tracking-wide"
                  >
                    ИГРАТА НА
                  </motion.div>
                </div>

                <div className="mask-line mt-2">
                  <motion.div
                    variants={line}
                    initial="hidden"
                    animate={loaded ? "show" : "hidden"}
                    custom={1}
                    className="font-display uppercase text-[#F5892E] text-[14vw] sm:text-[11vw] lg:text-[8.6vw] xl:text-[8rem] leading-[0.92] tracking-tight"
                  >
                    ИМОТИТЕ
                  </motion.div>
                </div>

                <div className="mask-line mt-6 mb-6 max-w-[52ch]">
                  <motion.p
                    variants={line}
                    initial="hidden"
                    animate={loaded ? "show" : "hidden"}
                    custom={3}
                    className="text-base sm:text-lg text-[#F5EBD9]/75 leading-relaxed"
                  >
                    Най-въздействащото събитие за имоти във Варна. Инвеститори, строители, брокери и предприемачи — на едно място.
                  </motion.p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 0.85, ease: [0.2, 0.8, 0.2, 1] }}
                  className="flex flex-wrap items-center gap-3 sm:gap-4"
                >
                  <button
                    data-testid="hero-cta-tickets"
                    onClick={goVipCheckout}
                    className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full text-sm sm:text-base font-bold tracking-wide uppercase bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C] text-[#0B1F3D] hover:scale-[1.03] transition-transform shadow-[0_15px_40px_-10px_rgba(224,185,92,0.7)]"
                  >
                    Вземи VIP билет
                    <Crown className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <button
                    data-testid="hero-cta-manifesto"
                    onClick={() => scrollTo("manifesto")}
                    className="px-7 py-3.5 rounded-full text-sm sm:text-base font-medium tracking-wide uppercase border border-[#F5EBD9]/30 text-[#F5EBD9] hover:bg-[#F5EBD9] hover:text-[#0B1F3D] transition-all duration-300"
                  >
                    Защо да се включиш?
                  </button>
                </motion.div>

                {/* Urgency strip: live countdown + seat progress + price anchor */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={loaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.9, delay: 1.05, ease: [0.2, 0.8, 0.2, 1] }}
                  data-testid="hero-urgency-strip"
                  className="mt-8 sm:mt-10 pt-6 border-t border-[#F5EBD9]/15 flex flex-wrap items-center gap-x-8 gap-y-5"
                >
                  <div className="flex items-center gap-3" data-testid="hero-mini-countdown">
                    <span className="relative flex h-2.5 w-2.5 shrink-0">
                      <span className="absolute inline-flex h-full w-full rounded-full animate-ping bg-[#E0B95C]" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#E0B95C]" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5EBD9]/60">
                      До старта:
                    </span>
                    <span className="font-display text-lg sm:text-xl text-[#F5892E] tabular-nums leading-none">
                      {String(left.d).padStart(2, "0")}д : {String(left.h).padStart(2, "0")}ч : {String(left.m).padStart(2, "0")}м : {String(left.s).padStart(2, "0")}с
                    </span>
                  </div>

                  <div className="min-w-[200px] flex-1 max-w-xs" data-testid="hero-seats-progress">
                    <div className="flex items-center justify-between font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.24em] text-[#F5EBD9]/60 mb-1.5">
                      <span>
                        <span className="text-[#F5EBD9]">{vipSold}</span> / {vipTotal} VIP места заети
                      </span>
                      <span className="text-[#E0B95C]">{vipPercent}%</span>
                    </div>
                    <div className="h-1.5 bg-[#F5EBD9]/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: "0%" }}
                        animate={loaded ? { width: `${vipPercent}%` } : {}}
                        transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1], delay: 1.2 }}
                        className="h-full rounded-full bg-gradient-to-r from-[#E0B95C] via-[#F5CE72] to-[#E0B95C]"
                      />
                    </div>
                  </div>

                  <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.24em] text-[#F5EBD9]/70" data-testid="hero-price-anchor">
                    VIP билет <span className="text-[#E0B95C] font-bold">119 €</span> · последни места на първите редове
                    <span className="block sm:inline sm:ml-3 mt-1 sm:mt-0 text-[#E5484D]">Стандартните билети (69 €) са изчерпани</span>
                  </div>
                </motion.div>
              </div>

              {portrait}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
